import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  ButtonGroup,
  Tooltip,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  useMap,
} from "react-leaflet";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";
import { dashboard } from "../../helpers/backend_helper";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDivisions,
  fetchRanges,
  fetchRounds,
  fetchBeats,
  fetchRanges2,
  fetchRounds2,
} from "../../store/actions";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import L from "leaflet";
import "jspdf-autotable";

import { Download } from "lucide-react";
import CanvasOverlay from "./CanvasOverlay";
import IncidentAnalysis from "./IncidentAnalysis";
import { hideLoader, showLoader } from "../../helpers/loaderService";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

// Map recenter component
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center.length === 2) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

export default function Analysis() {
  const dispatch = useDispatch();
  const reportRef = useRef(null);
  const { divisions, ranges, rounds } = useSelector(
    (state) => state.Common || {}
  );

  // State for tickets and filtering
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [groupedTickets, setGroupedTickets] = useState([]);
  const [oriantation, setOrientation] = useState("p");
  const [formData, setFormData] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    assignedUser: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  // Keep track of current filters for display in PDF
  const [activeFilters, setActiveFilters] = useState({});
  const [mapVisualization, setMapVisualization] = useState("none");
  // State for map data
  const [mapData, setMapData] = useState({
    type: "divisions", // 'divisions', 'ranges', 'rounds'
    coordinates: [],
    center: [19.9615, 79.2961],
    zoom: 17,
  });

  const [tooltipOpen, setTooltipOpen] = useState({
    email: false,
    whatsapp: false,
    download: false,
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [uniqueIncidentTypes, setUniqueIncidentTypes] = useState([]);
  const [searchIncidentType, setSearchIncidentType] = useState("");
  const [additionalStats, setAdditionalStats] = useState({});

  const GROUPING_OPTIONS = [
    {
      id: "incidentType",
      label: "Incident Type",
      key: "incidentTypeId",
      nameKey: "incidentName",
      showBurnedArea: true 
    },
    {
      id: "deathOrInjuryDetails",
      label: "Death/Injury Details",
      key: "deathOrInjuryDetails",
      nameKey: "deathOrInjuryDetails",
      colors: ["#FF6B6B", "#4ECDC4", "#FFD166"]
    },
    {
      id: "gender",
      label: "Gender Details",
      key: "gender",
      nameKey: "gender",
      colors: ["#FF6B6B", "#4ECDC4", "#FFD166"]
    },
    {
      id: "humanDetails",
      label: "Death/Injury Details",
      key: "humanDetails",
      nameKey: "humanDetails",
      colors: ["#FF6B6B", "#4ECDC4", "#FFD166"]
    },
    {
      id: "locationType",
      label: "Location Type",
      key: "locationType",
      nameKey: "locationType",
      colors: ["#6A0572", "#1A936F", "#3D5A80"]
    },
    {
      id: "killedByWildAnimal",
      label: "Wild Animal Incidents",
      key: "killedByWildAnimal",
      nameKey: "killedByWildAnimal",
      colors: ["#6A0572", "#1A936F", "#3D5A80", "#F86624"]
    }
  ];

  // Dropdown options
  const [filterOptions, setFilterOptions] = useState({
    divisions: [],
    ranges: [],
    rounds: [],
    beats: [],
    assignedUsers: [],
    statuses: [
      "open",
      "assigned",
      "acknowledged",
      "working",
      "resolved",
      "closed",
      "reopen",
    ],
  });

  const getIncidentIcon = (incidentTypeId, incidentTypeName = "") => {
    // Convert to lowercase for easier comparison
    const type =
      uniqueIncidentTypes.filter(
        (incident) => incident.incidentTypeId == incidentTypeId
      )[0]?.incidentName || incidentTypeName;

    // Create custom Leaflet icons for different incident types
    const createCustomIcon = (color) => {
      return new L.DivIcon({
        html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5);"></div>`,
        className: "custom-div-icon",
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        popupAnchor: [0, -8],
      });
    };

    // Check incident type and return appropriate icon
    if (type.includes("Fire")) {
      return createCustomIcon("#FF4500"); // Orange-Red for fire
    } else if (type.includes("Cattle Killing")) {
      return createCustomIcon("#8B0000"); // Dark Red for animal killing/poaching
    } else if (type.includes("Human Killing")) {
      return createCustomIcon("#006400"); // Dark Green for tree cutting/encroachment
    } else if (type.includes("Tiger Moment")) {
      return createCustomIcon("#1E90FF"); // Blue for animal sightings
    } else if (type.includes("Adoc. Call")) {
      return createCustomIcon("#FFD700"); // Gold for human-animal conflict
    } else {
      return createCustomIcon("#808080"); // Gray for unknown/other incidents
    }
  };

  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
    },
  ];

  // Set default date range (last 30 days)
  useEffect(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    setFormData((prev) => ({
      ...prev,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    }));
  }, []);

  // Convert coordinates array to leaflet format if needed
  const formatCoordinates = (coords) => {
    if (!coords || !Array.isArray(coords)) return [];
    return coords.map((coord) => [coord[1], coord[0]]); // Leaflet uses [lat, lng]
  };

  // Calculate center point of polygon
  const calculateCenter = (coords) => {
    if (!coords || !Array.isArray(coords) || coords.length === 0)
      return [19.9615, 79.2961];

    const latSum = coords.reduce((sum, point) => sum + point[0], 0);
    const lngSum = coords.reduce((sum, point) => sum + point[1], 0);

    return [latSum / coords.length, lngSum / coords.length];
  };

  // Modify handleInputChange to remove map data updates
  const handleInputChange = (e, key, fetchAction) => {
    const value = e.target.value;

    // Reset dependent dropdowns
    let updatedFormData = { ...formData, [key]: value };

    if (key === "division") {
      updatedFormData = {
        ...updatedFormData,
        range: "",
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch ranges
      if (value && value !== "0") {
        // Fetch ranges for this division
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "range") {
      updatedFormData = {
        ...updatedFormData,
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch rounds
      if (value && value !== "0") {
        // Fetch rounds for this range
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "round") {
      updatedFormData = {
        ...updatedFormData,
        beat: "",
      };
    }

    // Update form data
    setFormData(updatedFormData);
  };

  const toggleTooltip = (tooltipId) => {
    setTooltipOpen({
      ...tooltipOpen,
      [tooltipId]: !tooltipOpen[tooltipId],
    });
  };

  const fetchReports = async () => {
    const payload = {
      act: "dashboard",
      subact: "reports",
      info: {},
    };
    try {
      const response = await dashboard(payload);
      if (response?.message) {
        const sampleTickets = response?.data || [];
        const incidents = JSON.parse(
          localStorage.getItem("incidentTypes") || "[]"
        );
        setUniqueIncidentTypes(incidents);
        setTickets(sampleTickets);

        // Filter by default date range
        const dateFilteredTickets = filterTicketsByDateRange(
          sampleTickets,
          formData.startDate,
          formData.endDate
        );
        setFilteredTickets(dateFilteredTickets);

        // Group tickets initially
        groupTickets(dateFilteredTickets, formData);

        // Extract unique filter options
        const divisions = [
          ...new Set(sampleTickets.map((t) => t.divisionName)),
        ];
        const ranges = [...new Set(sampleTickets.map((t) => t.rangeName))];
        const rounds = [...new Set(sampleTickets.map((t) => t.roundName))];
        const beats = [...new Set(sampleTickets.map((t) => t.beatName))];
        const users = [...new Set(sampleTickets.map((t) => t.assignedUsers))];

        setFilterOptions({
          divisions,
          ranges,
          rounds,
          beats,
          assignedUsers: users,
          statuses: [
            "Open",
            "Assigned",
            "Acknowledged",
            "Working",
            "Resolved",
            "Closed",
            "Reopen",
          ],
        });
      }
    } catch (error) {
      toast.error("Error in fetching reports", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Generate PDF from the report with improved layout
  const generatePDF = async () => {
    if (!reportRef.current) return;
    showLoader();

    setIsGeneratingPDF(true);
  
    try {
      // Add a slight delay to ensure UI is settled
      await new Promise((resolve) => setTimeout(resolve, 100));
  
      // Define document settings
      const headerHeight = 20;
      const footerHeight = 10;
      const pagePadding = 10;
      // Fix the orientation variable and provide a default value
      const pdf = new jsPDF(activeFilters?.incidentType?.length>0?oriantation:"p", "mm", "a4", true);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
  
      // Create filter string for display
      const filterText = [];
      if (activeFilters.division && activeFilters.division !== "0") {
        const divName =
          divisions?.data?.find(
            (div) => div.divisionId == activeFilters.division
          )?.divisionName || activeFilters.division;
        filterText.push(`Division: ${divName}`);
      }
      if (activeFilters.range && activeFilters.range !== "0") {
        const rangeName =
          ranges?.data?.find((r) => r.rangeId == activeFilters.range)?.name ||
          activeFilters.range;
        filterText.push(`Range: ${rangeName}`);
      }
      if (activeFilters.round && activeFilters.round !== "0") {
        const roundName =
          rounds?.data?.find((r) => r.roundId == activeFilters.round)?.name ||
          activeFilters.roundName;
        filterText.push(`Round: ${roundName}`);
      }
      if (activeFilters.status && activeFilters.status !== "0") {
        filterText.push(`Status: ${activeFilters.status}`);
      }
      if (activeFilters.incidentType) {
        const typeName =
          uniqueIncidentTypes.find(
            (i) => i.incidentTypeId == activeFilters.incidentType
          )?.incidentName || activeFilters.incidentType;
        filterText.push(`Incident Type: ${typeName}`);
      }
  
      // Date range is always present
      const dateFormat = { year: "numeric", month: "short", day: "numeric" };
      const startDateFormatted = new Date(
        formData.startDate
      ).toLocaleDateString("en-US", dateFormat);
      const endDateFormatted = new Date(formData.endDate).toLocaleDateString(
        "en-US",
        dateFormat
      );
      filterText.push(`Date Range: ${startDateFormatted} to ${endDateFormatted}`);
  
      // Current date for report
      const reportDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
  
      // Function to add consistent header to each page
      const addPageHeader = (pageNum) => {
        // Light background color for header
        pdf.setFillColor(240, 240, 240);
        pdf.rect(0, 0, pdfWidth, headerHeight, "F");
  
        // Title with black bold text
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Control Room Reports - ${reportDate}`, pdfWidth / 2, 8, {
          align: "center",
        });
  
        // Applied filters
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        if (filterText.length > 0) {
          const filtersString = filterText.join(" | ");
          const splitFilters = pdf.splitTextToSize(filtersString, pdfWidth - 20);
          pdf.text(splitFilters[0], pdfWidth / 2, 14, { align: "center" });
          if (splitFilters.length > 1) {
            pdf.text(splitFilters[1], pdfWidth / 2, 18, { align: "center" });
          }
        }
  
        // Footer with page number
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${pageNum}`, pdfWidth / 2, pdfHeight - 8, {
          align: "center",
        });
      };
  
      // Get map element and legend element
      const mapContainer =
        reportRef.current.querySelector(".leaflet-container");
      const legendContainer = mapContainer.parentElement.nextElementSibling;
  
      if (!mapContainer) {
        throw new Error("Map element not found");
      }
  
      // IMPORTANT: Store original map properties and temporarily set it to 700px fixed height
      const originalMapHeight = mapContainer.style.height;
      const originalMapWidth = mapContainer.style.width;
      const originalPosition = mapContainer.style.position;
  
      // Set fixed size and force refresh if needed
      mapContainer.style.height = "2000px";
      mapContainer.style.width = "90%";
      mapContainer.style.position = "relative";
  
      // Force a redraw of the map if necessary
      const map = mapContainer.__leaflet_instance__;
      if (map && typeof map.invalidateSize === 'function') {
        map.invalidateSize(false);
      }
  
      // Capture the map directly
      const mapCanvas = await html2canvas(mapContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        ignoreElements: (element) => {
          // Ignore any loading indicators or unnecessary elements
          return (
            element.classList &&
            (element.classList.contains("loading-indicator") ||
              element.classList.contains("leaflet-control-attribution"))
          );
        },
      });
  
      // Capture the legend separately
      const legendCanvas = await html2canvas(legendContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });
  
      // Create a combined canvas
      const combinedCanvas = document.createElement("canvas");
      const ctx = combinedCanvas.getContext("2d");
  
      // Set combined canvas dimensions
      combinedCanvas.width = Math.max(mapCanvas.width, legendCanvas.width);
      combinedCanvas.height = mapCanvas.height + legendCanvas.height + 10; // 10px spacing
  
      // Fill with white background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
  
      // Draw map centered if needed
      const mapX =
        mapCanvas.width < combinedCanvas.width
          ? (combinedCanvas.width - mapCanvas.width) / 2
          : 0;
      ctx.drawImage(mapCanvas, mapX, 0);
  
      // Draw legend below map with styling
      ctx.fillStyle = "#f9f9f9";
      const legendY = mapCanvas.height + 10;
      const legendX = legendCanvas.width < combinedCanvas.width ? (combinedCanvas.width - legendCanvas.width) / 2 : 0;
      
      // Draw legend background
      ctx.fillRect(legendX, legendY, legendCanvas.width, legendCanvas.height);
      ctx.strokeStyle = "#eeeeee";
      ctx.lineWidth = 1;
      ctx.strokeRect(legendX, legendY, legendCanvas.width, legendCanvas.height);
  
      // Draw legend content
      ctx.drawImage(legendCanvas, legendX, legendY);
  
      // Restore original map properties immediately
      mapContainer.style.height = originalMapHeight;
      mapContainer.style.width = originalMapWidth;
      mapContainer.style.position = originalPosition;
  
      // Force map to refresh to its original size
      if (map && typeof map.invalidateSize == 'function') {
        map.invalidateSize(true);
      }
  
      // Give the map time to refresh before continuing
      await new Promise(resolve => setTimeout(resolve, 100));
  
      // PAGE 1: Map with legend page
      addPageHeader(1);
  
      // Add combined map and legend to first page - calculate dimensions to fill the page properly
      const availableMapHeight =
        pdfHeight - headerHeight - footerHeight - pagePadding * 2;
      const mapAspectRatio = combinedCanvas.width / combinedCanvas.height;
      let mapWidth = pdfWidth - pagePadding * 2;
      let mapHeight = mapWidth / mapAspectRatio;
  
      // If map height exceeds available space, scale it down
      if (mapHeight > availableMapHeight) {
        mapHeight = availableMapHeight;
        mapWidth = (mapHeight * mapAspectRatio)*(oriantation == "l" ? 1.2 : 1);
  
        // Center the map horizontally if it's not full width
        const leftMargin = ((pdfWidth - mapWidth) / 2);
        pdf.addImage(
          combinedCanvas.toDataURL("image/png"),
          "PNG",
          leftMargin,
          headerHeight + pagePadding,
          mapWidth,
          mapHeight
        );
      } else {
        // Map fits with full width
        pdf.addImage(
          combinedCanvas.toDataURL("image/png"),
          "PNG",
          pagePadding,
          headerHeight + pagePadding,
          mapWidth,
          mapHeight
        );
      }
  
      // Now capture the incident analysis separately
      const incidentAnalysisElement = reportRef.current.querySelector(".incident-analysis");
      
      if (incidentAnalysisElement) {
        // Add a new page for content
        pdf.addPage();
        addPageHeader(2);
  
        const contentCanvas = await html2canvas(incidentAnalysisElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });
  
        // Calculate content dimensions for proper scaling
        const contentWidth = contentCanvas.width;
        const contentHeight = contentCanvas.height;
        const availableContentHeight = pdfHeight - headerHeight - footerHeight - (pagePadding * 2.5);
        
        // Calculate scale to fit width
        const contentScale = Math.min((pdfWidth - (pagePadding * 2)) / contentWidth, 1);
        const scaledContentWidth = contentWidth * contentScale;
        const scaledContentHeight = contentHeight * contentScale *0.90;
        
        // Determine if we need multiple pages
        const pagesNeeded = Math.ceil(scaledContentHeight / availableContentHeight);
        
        // If content fits on one page
       // Replace the multi-page handling section with this improved version:
if (pagesNeeded === 1) {
  pdf.addImage(
    contentCanvas.toDataURL("image/png"),
    "PNG",
    pagePadding,
    headerHeight + pagePadding,
    scaledContentWidth,
    scaledContentHeight
  );
} else {
  // Multi-page handling with consistent scaling
  let currentPage = 2; // We're already on page 2 at this point
  
  // Calculate the height in the source canvas that can fit on one PDF page
  const heightPerPage = availableContentHeight / contentScale;
  
  for (let i = 0; i < pagesNeeded; i++) {
    // Calculate source rectangle to clip
    const sourceY = i * heightPerPage;
    const sourceHeight = Math.min(heightPerPage, contentHeight - sourceY);
    
    // Important: Use the SAME scaledContentWidth for all pages to maintain consistency
    const destHeight = sourceHeight * contentScale;
    
    // Add the image section to PDF with consistent scaling
    pdf.addImage(
      contentCanvas.toDataURL("image/png"),
      "PNG",
      pagePadding,
      headerHeight + pagePadding,
      scaledContentWidth,  // Same width for all pages
      destHeight,
      null,
      "FAST",
      0,
      sourceY,
      contentWidth,
      sourceHeight
    );
    
    // If we have more content, add another page
    if (i < pagesNeeded - 1) {
      pdf.addPage();
      addPageHeader(currentPage + 1);
      currentPage++;
    }
  }
}
      }
  
      pdf.save("control-room-report.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error generating PDF report", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      hideLoader();

      setIsGeneratingPDF(false);
    }
  };

  // Filter tickets by date range
  const filterTicketsByDateRange = (tickets, startDate, endDate) => {
    if (!startDate || !endDate) return tickets;

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return tickets.filter((ticket) => {
      const ticketDate = new Date(ticket.createdAt || ticket.timestamp);
      return ticketDate >= start && ticketDate <= end;
    });
  };

  // Group tickets based on selection
  const groupTickets = (filteredTickets, currentFormData) => {
    let groupedData = [];

    // Helper function to calculate status counts
    const getStatusCounts = (tickets) => {
      return {
        open: tickets.filter((t) => t.status.toLowerCase() === "open").length,
        inProgress: tickets.filter((t) =>
          ["working", "assigned", "acknowledged"].includes(
            t.status.toLowerCase()
          )
        ).length,
        resolved: tickets.filter((t) =>
          ["closed", "resolved"].includes(t.status.toLowerCase())
        ).length,
      };
    };

    const getCategoryCounts = (tickets = []) => {
      const { key, nameKey, id } = GROUPING_OPTIONS;
      const categoryCounts = {};
    
      tickets.forEach((ticket) => {
        const categoryKey = ticket[key] || "unknown";
    
        if (!categoryCounts[categoryKey]) {
          let categoryName = categoryKey;
    
          // Special case for incidentTypeId: fetch name from `uniqueIncidentTypes`
          if (id === "incidentType") {
            categoryName =
              uniqueIncidentTypes.find(
                (incident) => incident.incidentTypeId === categoryKey
              )?.incidentName || "Unknown";
          } else {
            categoryName = ticket[nameKey] || categoryKey || "Unknown";
          }
    
          categoryCounts[categoryKey] = {
            count: 0,
            name: categoryName,
          };
        }
    
        categoryCounts[categoryKey].count++;
      });
    
      return categoryCounts;
    };
    

    // Helper function to calculate incident type counts
    const getIncidentTypeCounts = (tickets) => {
      const incidentCounts = {};

      tickets.forEach((ticket) => {
        const incidentTypeId = ticket.incidentTypeId || "unknown";
        if (!incidentCounts[incidentTypeId]) {
          // Find the incident type name from the uniqueIncidentTypes array
          const incidentName =
            uniqueIncidentTypes.find(
              (incident) => incident.incidentTypeId == incidentTypeId
            )?.incidentName || "Unknown";

          incidentCounts[incidentTypeId] = {
            count: 0,
            name: incidentName,
          };
        }
        incidentCounts[incidentTypeId].count++;
      });

      return incidentCounts;
    };

    // If division is selected (including when division is selected and range is "All")
    if (currentFormData.division && currentFormData.division !== "0") {
      // If range is also selected but not round (or round is "All")
      if (currentFormData.range && currentFormData.range !== "0") {
        // Group by round
        const groupByRound = filteredTickets.reduce((acc, ticket) => {
          const key = ticket.roundName || "Unknown Round";
          if (!acc[key]) {
            acc[key] = {
              tickets: [],
              name: key,
              group: "Round",
              totalBurnedArea: 0,
            };
          }
          acc[key].tickets.push(ticket);
          if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
            const burnedArea = parseFloat(ticket.burnedArea);
            if (!isNaN(burnedArea)) {
              acc[key].totalBurnedArea += burnedArea;
            }
          }
          return acc;
        }, {});

        // Convert grouped object to array for display with status counts
        groupedData = Object.keys(groupByRound).map((roundName) => {
          const roundTickets = groupByRound[roundName].tickets;
          const statusCounts = getStatusCounts(roundTickets);
          const incidentTypeCounts = getIncidentTypeCounts(roundTickets);
          const catergotyCounts = getCategoryCounts(roundTickets);
          console.log("categoryCounts", catergotyCounts);
          return {
            name: roundName,
            count: roundTickets.length,
            group: "Round",
            openCount: statusCounts.open,
            inProgressCount: statusCounts.inProgress,
            resolvedCount: statusCounts.resolved,
            incidentTypeCounts: incidentTypeCounts,
            totalBurnedArea: groupByRound[roundName].totalBurnedArea || 0,
          };
        });
      } else {
        // Group by range when division is selected but range is not (or range is "All")
        const groupByRange = filteredTickets.reduce((acc, ticket) => {
          const key = ticket.rangeId;
          if (!acc[key]) {
            acc[key] = {
              tickets: [],
              id: key,
              name: ticket.rangeName || "Unknown Range",
              group: "Range",
              totalBurnedArea: 0,
            };
          }
          acc[key].tickets.push(ticket);
          if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
            const burnedArea = parseFloat(ticket.burnedArea);
            if (!isNaN(burnedArea)) {
              acc[key].totalBurnedArea += burnedArea;
            }
          }
          return acc;
        }, {});

        // Convert grouped object to array for display with status counts
        groupedData = Object.keys(groupByRange).map((rangeId) => {
          const rangeTickets = groupByRange[rangeId].tickets;
          const rangeName =
            dependentDropdowns
              .filter((item) => item.key == "range")[0]
              ?.data?.data?.filter((item) => item?.rangeId == rangeId)[0]
              ?.name || groupByRange[rangeId].name;
          const statusCounts = getStatusCounts(rangeTickets);
          const incidentTypeCounts = getIncidentTypeCounts(rangeTickets);

          return {
            rangeId,
            name: rangeName,
            count: rangeTickets.length,
            group: "Range",
            openCount: statusCounts.open,
            inProgressCount: statusCounts.inProgress,
            resolvedCount: statusCounts.resolved,
            incidentTypeCounts: incidentTypeCounts,
            totalBurnedArea: groupByRange[rangeId].totalBurnedArea || 0,
          };
        });
      }
    }
    // If no division is selected, group by division
    else {
      // Group by division
      const groupByDivision = filteredTickets.reduce((acc, ticket) => {
        const key = ticket.divisionId;
        if (!acc[key]) {
          acc[key] = {
            tickets: [],
            key: key,
            name: ticket.divisionName || "Unknown Division",
            group: "Division",
            totalBurnedArea: 0,
          };
        }
        acc[key].tickets.push(ticket);
        if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
          const burnedArea = parseFloat(ticket.burnedArea);
          if (!isNaN(burnedArea)) {
            acc[key].totalBurnedArea += burnedArea;
          }
        }
        return acc;
      }, {});

      // Convert grouped object to array for display with status counts
      groupedData = Object.keys(groupByDivision).map((divisionName) => {
        const divisionTickets = groupByDivision[divisionName].tickets;
        const divisionNamea =
          dependentDropdowns
            .filter((item) => item.key == "division")[0]
            ?.data?.data?.filter((item) => item?.divisionId == divisionName)[0]
            ?.divisionName || groupByDivision[divisionName].name;
        const statusCounts = getStatusCounts(divisionTickets);
        const incidentTypeCounts = getIncidentTypeCounts(divisionTickets);
        const catergotyCounts = getCategoryCounts(divisionTickets);
        console.log("categoryCounts", catergotyCounts);

        return {
          name: divisionNamea || "Unknown Division",
          count: divisionTickets.length,
          group: "Division",
          openCount: statusCounts.open,
          inProgressCount: statusCounts.inProgress,
          resolvedCount: statusCounts.resolved,
          incidentTypeCounts: incidentTypeCounts,
          totalBurnedArea: groupByDivision[divisionName].totalBurnedArea || 0,
        };
      });
    }

    setGroupedTickets(groupedData);
  };

  // Filter tickets based on all criteria
  const filterTickets = (currentFormData = formData) => {
    let filtered = [...tickets];

    // Apply date range filter
    filtered = filterTicketsByDateRange(
      filtered,
      currentFormData.startDate,
      currentFormData.endDate
    );

    // Apply division filter
    if (currentFormData.division && currentFormData.division !== "0") {
      filtered = filtered.filter(
        (t) => t.divisionId == currentFormData.division
      );
    }

    // Apply range filter
    if (currentFormData.range && currentFormData.range !== "0") {
      filtered = filtered.filter((t) => t.rangeId == currentFormData.range);
    }

    // Apply round filter
    if (currentFormData.round && currentFormData.round !== "0") {
      filtered = filtered.filter((t) => t.roundId == currentFormData.round);
    }

    // Apply status filter
    if (currentFormData.status && currentFormData.status !== "0") {
      filtered = filtered.filter(
        (t) => t.status.toLowerCase() === currentFormData.status.toLowerCase()
      );
    }

    // Apply incident type filter
    if (searchIncidentType) {
      filtered = filtered.filter((t) => t.incidentTypeId == searchIncidentType);
    }

    setFilteredTickets(filtered);

    // Also update the grouped tickets
    groupTickets(filtered, currentFormData);
  };

  // Load initial dropdown data and set up map
  useEffect(() => {
    dispatch(fetchDivisions());

    // Only try to load initial data if we have form values on first render
    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));

      if (range) {
        dispatch(fetchRounds(range));

        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }

    fetchReports();
  }, []);

  // Update map data when divisions are loaded
  useEffect(() => {
    if (divisions?.data?.length > 0) {
      const allCoordinates = divisions.data
        .filter((div) => div.coordinates && Array.isArray(div.coordinates))
        .map((div) => formatCoordinates(div.coordinates));

      setMapData({
        type: "divisions",
        coordinates: allCoordinates,
        center: [19.9615, 79.2961],
        zoom: 10,
      });
    }
  }, [divisions]);

  // Apply filters when button is clicked
  const applyFilters = () => {
    // Save the current filters for PDF display
    setActiveFilters({
      ...formData,
      incidentType: searchIncidentType,
    });

    // Apply filters
    filterTickets();

    // Update map data based on current filter selections
    updateMapData();
  };

  const updateMapData = () => {
    // Default map view (all divisions)
    let newMapData = {
      type: "divisions",
      coordinates:
        divisions?.data?.map((div) => formatCoordinates(div.coordinates)) || [],
      center: [20.5937, 78.9629],
      zoom: 10,
    };

    // If division is selected
    if (formData.division && formData.division !== "0") {
      const selectedDivision = divisions?.data?.find(
        (div) => div.divisionId == formData.division
      );
      if (selectedDivision && selectedDivision.coordinates) {
        const formattedCoords = formatCoordinates(selectedDivision.coordinates);
        const center = calculateCenter(formattedCoords);
        newMapData = {
          type: "division",
          coordinates: [formattedCoords],
          center: center,
          zoom: 11,
        };

        // If range is selected
        if (formData.range && formData.range !== "0") {
          const selectedRange = ranges?.data?.find(
            (range) => range.rangeId == formData.range
          );
          if (selectedRange && selectedRange.coordinates) {
            const formattedCoords = formatCoordinates(
              selectedRange.coordinates
            );
            const center = calculateCenter(formattedCoords);
            newMapData = {
              type: "range",
              coordinates: [formattedCoords],
              center: center,
              zoom: 12,
            };

            // If round is selected
            if (formData.round && formData.round !== "0") {
              const selectedRound = rounds?.data?.find(
                (round) => round.roundId == formData.round
              );
              if (selectedRound && selectedRound.coordinates) {
                const formattedCoords = formatCoordinates(
                  selectedRound.coordinates
                );
                const center = calculateCenter(formattedCoords);
                newMapData = {
                  type: "round",
                  coordinates: [formattedCoords],
                  center: center,
                  zoom: 12,
                };
              }
            }
          }
        }
      }
    }

    // Update map data
    setMapData(newMapData);
  };

  const resetFilters = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    const resetFormData = {
      division: "",
      range: "",
      round: "",
      beat: "",
      assignedUser: "",
      status: "",
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };

    // Clear active filters
    setActiveFilters({});

    // Reset form data
    setFormData(resetFormData);
    setSearchIncidentType("");

    // Reset map view to show all divisions
    setMapData({
      type: "divisions",
      coordinates:
        divisions?.data?.map((div) => formatCoordinates(div.coordinates)) || [],
      center: [20.5937, 78.9629],
      zoom: 10,
    });

    // Reset to filtered tickets by date only
    const dateFilteredTickets = filterTicketsByDateRange(
      tickets,
      resetFormData.startDate,
      resetFormData.endDate
    );
    setFilteredTickets(dateFilteredTickets);

    // Reset grouped tickets
    groupTickets(dateFilteredTickets, resetFormData);
  };
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="Analysis" />

        {/* Filters Section */}
        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col md={6}>
                <h4 className="card-title">Filters</h4>
              </Col>
            </Row>

            <Row>
              {/* Date range inputs */}
              <Col md={2}>
                <FormGroup>
                  <Label for="startDate">Start Date</Label>
                  <Input
                    type="date"
                    name="startDate"
                    id="startDate"
                    max={formData.endDate} // Add this line to set maximum date
                    value={formData.startDate}
                    onChange={(e) => handleInputChange(e, "startDate", null)}
                  />
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label for="endDate">End Date</Label>
                  <Input
                    type="date"
                    name="endDate"
                    id="endDate"
                    min={formData.startDate} // Add this line to set minimum date
                    value={formData.endDate}
                    onChange={(e) => handleInputChange(e, "endDate", null)}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>Incident Type</Label>
                  <Input
                    type="select"
                    value={searchIncidentType}
                    onChange={(e) => setSearchIncidentType(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {uniqueIncidentTypes.map((incident) => (
                      <option key={incident.id} value={incident.incidentTypeId}>
                        {incident.incidentName}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Dependent Dropdowns */}
              {dependentDropdowns.map(({ label, key, data, fetch }, index) => (
                <Col md={2} key={key}>
                  <FormGroup>
                    <Label for={key}>{label}</Label>
                    <Input
                      type="select"
                      id={key}
                      value={formData[key] || ""}
                      onChange={(e) => handleInputChange(e, key, fetch)}
                      disabled={
                        (index > 0 &&
                          !formData[
                            ["division", "range", "round"][index - 1]
                          ]) ||
                        (index > 0 && formData["division"] === "0")
                      }
                    >
                      <option value="0">All</option>
                      {Array.isArray(data?.data) &&
                        data.data.map((item) => (
                          <option
                            key={item[`${key}Id`]}
                            value={item[`${key}Id`]}
                          >
                            {item.name ||
                              item.divisionName ||
                              item.rangeName ||
                              item.beatName ||
                              item.roundName}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
              ))}

              <Col md={2}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={formData.status || ""}
                    onChange={(e) => handleInputChange(e, "status", null)}
                  >
                    <option value="0">All Status</option>
                    {filterOptions?.statuses.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Button color="primary" onClick={applyFilters}>
                  Apply
                </Button>
                <Button
                  color="secondary"
                  className="ms-2"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Col>
              <Col md={6} className="text-end">
        <Label className="mr-4">
          <Input
            type="radio"
            name="orientation"
            value="p"
            checked={oriantation === 'p'}
            onChange={(e) => setOrientation(e.target.value)}
          />{' '}
          Portrait
        </Label>
        <Label className="mx-3">
          <Input
            type="radio"
            name="orientation"
            value="l"
            checked={oriantation === 'l'}
            onChange={(e) => setOrientation(e.target.value)}
          />{' '}
          Landscape
        </Label>
                <ButtonGroup>
                  <Button
                    color="primary"
                    id="downloadPdfBtn"
                    onClick={generatePDF}
                    disabled={isGeneratingPDF}
                  >
                    <Download size={16} className="me-1" />
                    {isGeneratingPDF ? "Generating..." : "Download PDF"}
                  </Button>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.download}
                    target="downloadPdfBtn"
                    toggle={() => toggleTooltip("download")}
                  >
                    Download report as PDF
                  </Tooltip>
                </ButtonGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Active Filters Display - This will be useful for users but won't be part of the PDF */}
        {Object.keys(activeFilters).some(
          (key) =>
            activeFilters[key] &&
            activeFilters[key] !== "0" &&
            key !== "startDate" &&
            key !== "endDate"
        ) && (
          <Card className="mb-3">
            <CardBody>
              <h5>Active Filters:</h5>
              <div className="d-flex flex-wrap gap-2">
                {activeFilters.division && activeFilters.division !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Division:{" "}
                    {divisions?.data?.find(
                      (div) => div.divisionId == activeFilters.division
                    )?.divisionName || activeFilters.division}
                  </div>
                )}

                {activeFilters.range && activeFilters.range !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Range:{" "}
                    {ranges?.data?.find((r) => r.rangeId == activeFilters.range)
                      ?.name || activeFilters.range}
                  </div>
                )}

                {activeFilters.round && activeFilters.round !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Round:{" "}
                    {rounds?.data?.find((r) => r.roundId == activeFilters.round)
                      ?.name || activeFilters.round}
                  </div>
                )}

                {activeFilters.status && activeFilters.status !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Status: {activeFilters.status}
                  </div>
                )}

                {activeFilters.incidentType && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Incident Type:{" "}
                    {uniqueIncidentTypes.find(
                      (i) => i.incidentTypeId == activeFilters.incidentType
                    )?.incidentName || activeFilters.incidentType}
                  </div>
                )}

                {/* <Button
                  color="link"
                  size="sm"
                  className="p-0 ms-2"
                  onClick={resetFilters}
                >
                  Clear All
                </Button> */}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Report content to be captured for PDF */}
        <div ref={reportRef}>
          <div className="d-none">
            <h1 className="text-center mb-4">Control Room Dashboard Report</h1>
            <p className="text-center mb-4">
              Generated on:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          <div className="pdf-header d-none"></div>

          {/* Map Section */}
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Incident Locations</h4>
              <em style={{color:'red'}}>Please Adjust the map before generating PDF.</em>
              <div>
                <div
                  style={{ display: "flex", height: "2000px", width: "100%" }}
                >
                  <MapContainer
                    center={mapData?.center}
                    zoom={mapData?.zoom}
                    preferCanvas={true}
                    renderer={L.canvas()}
                    style={{
                      height: "100%",
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <TileLayer
                      attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapController
                      center={mapData.center}
                      zoom={mapData.zoom}
                    />

                    <CanvasOverlay
                      data={filteredTickets}
                      drawMethod={mapVisualization} // You'll need to add this state variable
                    />
                    {mapData?.coordinates &&
                      mapData?.coordinates.map(
                        (coords, index) =>
                          coords &&
                          coords.length > 0 && (
                            <Polygon
                              key={index}
                              positions={coords}
                              pathOptions={{
                                color:
                                  mapData.type == "division"
                                    ? "blue"
                                    : mapData.type === "range"
                                    ? "green"
                                    : mapData.type === "round"
                                    ? "purple"
                                    : "orange",
                                fillColor:
                                  mapData.type == "division"
                                    ? "blue"
                                    : mapData.type === "range"
                                    ? "green"
                                    : mapData.type === "round"
                                    ? "purple"
                                    : "orange",
                                fillOpacity: 0.2,
                                weight: 2,
                              }}
                            />
                          )
                      )}
                    {filteredTickets.map(
                      (ticket, index) =>
                        ticket?.incidentLocation &&
                        ticket?.incidentLocation?.lat &&
                        ticket?.incidentLocation?.long && (
                          <Marker
                            key={`marker-${index}`}
                            position={[
                              ticket?.incidentLocation?.lat,
                              ticket?.incidentLocation?.long,
                            ]}
                            icon={getIncidentIcon(
                              ticket.incidentTypeId,
                              ticket.incidentTypeName
                            )}
                          >
                            <Popup>
                              <div>
                                <h6>Ticket: {ticket.serialId || "N/A"}</h6>
                                <p>Status: {ticket.status || "N/A"}</p>
                                <p>Division: {ticket.divisionName || "N/A"}</p>
                                <p>Range: {ticket.rangeName || "N/A"}</p>
                                <p>Round: {ticket.roundName || "N/A"}</p>
                                {ticket.description && (
                                  <p>
                                    Description:{" "}
                                    {ticket.description.substring(0, 100)}...
                                  </p>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        )
                    )}
                  </MapContainer>
                </div>
                <div>
                  <ul className="d-flex mt-2 flex-wrap list-unstyled">
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#FF4500",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Forest Fire</span>
                    </li>
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#8B0000",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Cattle Killing</span>
                    </li>
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#1E90FF",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Tiger Moment</span>
                    </li>
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#006400",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Human Killing</span>
                    </li>
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#FFD700",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Adoc. Call</span>
                    </li>
                    <li className="me-3 mb-2 d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#808080",
                          border: "2px solid white",
                          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                          marginRight: 5,
                        }}
                      ></div>
                      <span>Animal Killing</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardBody>
          </Card>

          {filteredTickets?.length > 0 && (
            <Card className="mt-4 incident-analysis">
              <CardBody>
                <IncidentAnalysis
                  filteredTickets={filteredTickets}
                  groupedTickets={groupedTickets}
                  uniqueIncidentTypes={uniqueIncidentTypes}
                  searchIncidentType={searchIncidentType}
                />
              </CardBody>
            </Card>
          )}

          <div className="pdf-footer d-none"></div>
        </div>
      </Container>
    </div>
  );
}
